/* lib/chat/kitchenEditRight.js — право правки «Продукции» (kitchen.edit) в ассистенте
   (pos-base-rework T9). ЧИСТАЯ логика без React и window.

   Откуда право. Карта эффективных прав текущего юзера приходит в GET /api/integrations/
   полем `access` ({key: bool}, contracts/integrations-status.json) — та же, что читает
   «Продукция» (screens/kitchen/kitchenMode.canEditKitchen). У чата своего контекста
   интеграции нет, поэтому ChatProvider читает статус сам через lib/adapters/integrations и
   держит `canEditKitchen` в значении контекста. Карты нет (личное пространство, статус не
   пришёл или упал) — правка ОТКРЫТА (умолчание каталога lib/analyticsAccess.canKitchen);
   явное false — закрыта. Решает всегда сервер: любая запись Продукции без права — 403
   {detail, code: 'kitchen_edit_forbidden'} (contracts/kitchen-edit-403.json); здесь — чтобы
   не предлагать кнопку, которую сервер отклонит, и показать его detail, если всё же отклонил.

   Что в чате — запись Продукции (зеркало бэкенда, chats/views.py; тест сверяет файл):
   · скиллы-создатели ttk-generate / menu-design (KITCHEN_CREATE_SKILLS): создание чата и ход в нём;
   · inline-черновики (draft/commit) — всегда (виды card/ttk/menu);
   · подготовленные правки (pending-changes/<id>/commit/): виды ttk/card/menu всегда,
     действие — если из KITCHEN_EDIT_ACTIONS. Планы, настройки, обращения, уведомления,
     команда — не Продукция, их кнопки остаются. */

// Ключ карты access и правило — зеркало lib/analyticsAccess.canKitchen(access, 'edit')
// (kitchenKey('edit'), kitchenDefault('edit') = true; тест сверяет паритет на всех формах).
// Сам каталог сюда НЕ импортируем: модуль живёт в провайдере чата (главный чанк), а каталог
// с подписями вкладок и секций нужен только экранам команды и «Продукции».
export const KITCHEN_EDIT_KEY = 'kitchen.edit';

// Подпись вместо кнопок «Утвердить»/«Добавить» и запасной текст тоста. Та же строка, что
// KITCHEN_EDIT_CLOSED_TEXT редакторов (screens/kitchen/kitchenMode.js; тест сверяет) — без
// финальной точки, detail сервера приходит с ней.
export const KITCHEN_EDIT_CLOSED_TEXT = 'Правка Продукции закрыта администратором пространства';
export const KITCHEN_EDIT_FORBIDDEN_CODE = 'kitchen_edit_forbidden';

// Скиллы, материализующие ТТК/меню (chats/views.py KITCHEN_CREATE_SKILLS).
export const KITCHEN_CREATE_SKILLS = ['ttk-generate', 'menu-design'];
// Виды подготовленных правок — сущности Продукции.
export const KITCHEN_PENDING_KINDS = ['ttk', 'card', 'menu'];
// Действия с подтверждением, пишущие в Продукцию (chats/views.py KITCHEN_EDIT_ACTIONS).
export const KITCHEN_EDIT_ACTIONS = [
  'update_card_prices', 'create_pos_link', 'create_pos_links',
  'create_cards_from_invoices', 'update_selling_prices', 'update_menu_stop_list',
  'restore_version',
];

const isObj = (v) => !!v && typeof v === 'object';

/* Карта прав из ответа GET /api/integrations/ (или null — карты нет). */
export function kitchenAccessOf(status) {
  return isObj(status) && isObj(status.access) ? status.access : null;
}

/* Право правки по карте: нет карты/ключа → открыто (умолчание каталога); явное false →
   закрыто; true и любое не-bool → открыто. То же, что canKitchen(access, 'edit'). */
export function canEditKitchenFromAccess(access) {
  if (!isObj(access)) return true;
  return access[KITCHEN_EDIT_KEY] !== false;
}

/* Право по СОСТОЯНИЮ запроса карты (useFakeQuery). Смена ключа (другое пространство) — хук
   держит data прежнего ключа при loading=true, refreshing=false: это карта ЧУЖОГО пространства,
   считаем «неизвестно» (открыто, решает сервер), а не применяем её (S10). Перезапрос того же
   ключа (refetchOnFocus) — refreshing=true, прежняя карта верна и остаётся. */
export function canEditKitchenFromQuery(q) {
  if (!isObj(q)) return true;
  const switching = !!q.loading && !q.refreshing;
  return canEditKitchenFromAccess(switching ? null : q.data);
}

/* Текст отказа «правка закрыта» или null, если ошибка о другом. Принимает конверт apiFetch
   ({ok, status, data}), ошибку unwrap (e.code/e.detail) и кадр ошибки стрима ({code, detail}). */
export function kitchenEditForbiddenDetail(x) {
  if (!isObj(x)) return null;
  const src = isObj(x.data) ? x.data : x;
  if (src.code !== KITCHEN_EDIT_FORBIDDEN_CODE) return null;
  const detail = typeof src.detail === 'string' ? src.detail.trim() : '';
  return detail || KITCHEN_EDIT_CLOSED_TEXT;
}

export function isKitchenCreateSkill(slug) {
  return typeof slug === 'string' && KITCHEN_CREATE_SKILLS.includes(slug);
}

/* Галерея скиллов пустого чата: без права — без создателей Продукции (свободный чат и
   отчётные скиллы остаются); право открыто или неизвестно — список как есть. */
export function skillsForKitchenEdit(skills, canEdit) {
  const list = Array.isArray(skills) ? skills : [];
  if (canEdit !== false) return list;
  return list.filter((s) => !isKitchenCreateSkill(s && s.slug));
}

/* Нужна ли подтверждению правки запись Продукции (см. шапку). Действие читаем из
   preview.action (форма адаптера) или ops.action (сырая форма бэкенда). */
export function isKitchenPendingChange(pc) {
  if (!isObj(pc)) return false;
  if (KITCHEN_PENDING_KINDS.includes(pc.kind)) return true;
  if (pc.kind !== 'action') return false;
  const action = (isObj(pc.preview) && pc.preview.action) || (isObj(pc.ops) && pc.ops.action) || '';
  return KITCHEN_EDIT_ACTIONS.includes(String(action));
}

/* Кнопки «Подтвердить/Утвердить» у правки прятать: правка Продукции при явно закрытом праве. */
export function pendingEditClosed(pc, canEdit) {
  return canEdit === false && isKitchenPendingChange(pc);
}
